import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const defaultTransactionForm = {
  description: '',
  merchant_name: '',
  amount_total: '',
  installment_count: 1,
  purchase_date: '',
  category: '',
};

const defaultSimulationForm = {
  description: '',
  merchant_name: '',
  amount_total: '',
  installment_count: 1,
  purchase_date: '',
};

function formatDate(value) {
  return value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : 'Nao informado';
}

const PersonalFinanceCardsPage = () => {
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingTransaction, setSavingTransaction] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [detail, setDetail] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [transactionForm, setTransactionForm] = useState(defaultTransactionForm);
  const [simulationForm, setSimulationForm] = useState(defaultSimulationForm);

  const loadCardDetail = async (cardId) => {
    if (!cardId) {
      setDetail(null);
      return;
    }

    try {
      setDetailLoading(true);
      const response = await axios.get(`/api/personal-finance/card-accounts/${cardId}`);
      setDetail(response.data || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar detalhe do cartao');
    } finally {
      setDetailLoading(false);
    }
  };

  const loadCards = async (preferredCardId = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/card-accounts');
      const nextCards = Array.isArray(response.data) ? response.data : [];
      const nextSelectedId =
        preferredCardId ||
        (nextCards.some((item) => item.id === selectedCardId) ? selectedCardId : nextCards[0]?.id || '');

      setCards(nextCards);
      setSelectedCardId(nextSelectedId);
      await loadCardDetail(nextSelectedId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar cartoes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCards();
  }, []);

  const selectedCard = cards.find((item) => item.id === selectedCardId) || null;

  const handleCreateTransaction = async () => {
    if (!selectedCardId) return;

    try {
      setSavingTransaction(true);
      await axios.post(`/api/personal-finance/card-accounts/${selectedCardId}/transactions`, {
        description: transactionForm.description,
        merchant_name: transactionForm.merchant_name || undefined,
        amount_total: Number(transactionForm.amount_total || 0),
        installment_count: Number(transactionForm.installment_count || 1),
        purchase_date: transactionForm.purchase_date || undefined,
        category: transactionForm.category || undefined,
      });
      toast.success('Compra mockada criada');
      setTransactionForm(defaultTransactionForm);
      await loadCards(selectedCardId);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar compra mockada');
    } finally {
      setSavingTransaction(false);
    }
  };

  const handleSimulatePurchase = async () => {
    if (!selectedCardId) return;

    try {
      setSimulating(true);
      const response = await axios.post(`/api/personal-finance/card-accounts/${selectedCardId}/simulate-purchase`, {
        description: simulationForm.description || undefined,
        merchant_name: simulationForm.merchant_name || undefined,
        amount_total: Number(simulationForm.amount_total || 0),
        installment_count: Number(simulationForm.installment_count || 1),
        purchase_date: simulationForm.purchase_date || undefined,
      });
      setSimulation(response.data || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao simular compra');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Cartoes ativos</p>
            <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : String(cards.length)}</p>
            <p className="text-sm text-slate-500">Cartoes prontos para operating layer de fatura</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Limite total</p>
            <p className="text-2xl font-semibold text-slate-900">
              {selectedCard?.credit_limit === null || selectedCard?.credit_limit === undefined
                ? '--'
                : currency.format(Number(selectedCard.credit_limit || 0))}
            </p>
            <p className="text-sm text-slate-500">Capacidade de credito do cartao selecionado</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body space-y-2">
            <p className="text-sm text-slate-500">Limite projetado</p>
            <p className="text-2xl font-semibold text-slate-900">
              {detail?.limits?.available_limit_projected === null || detail?.limits?.available_limit_projected === undefined
                ? '--'
                : currency.format(Number(detail.limits.available_limit_projected || 0))}
            </p>
            <p className="text-sm text-slate-500">Disponibilidade apos compras mockadas registradas</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr_1.4fr]">
        <div className="card">
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Operating layer</p>
                <h2 className="text-xl font-semibold text-slate-900">Cartoes</h2>
              </div>
              <button className="btn-secondary" onClick={() => loadCards(selectedCardId)} disabled={loading}>
                Atualizar
              </button>
            </div>

            {!loading && cards.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                Nenhum cartao canonico disponivel. Conecte uma base ou faca seed manual em `Conexoes`.
              </div>
            )}

            <div className="space-y-3">
              {cards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  className={`w-full rounded-2xl border p-4 text-left transition ${
                    selectedCardId === card.id ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  onClick={() => {
                    setSelectedCardId(card.id);
                    loadCardDetail(card.id);
                    setSimulation(null);
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{card.display_name}</p>
                      <p className="mt-1 text-sm text-slate-500">
                        {card.brand || 'Sem bandeira'} / final {card.last4 || '----'}
                      </p>
                    </div>
                    <span className="badge-info">{card.institution_name || card.connection_display_name}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-1 text-sm text-slate-600">
                    <p>Limite: {card.credit_limit === null ? 'Nao informado' : currency.format(Number(card.credit_limit || 0))}</p>
                    <p>Fechamento / vencimento: {card.closing_day || '--'} / {card.due_day || '--'}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Detalhe</p>
                <h2 className="text-xl font-semibold text-slate-900">
                  {selectedCard ? selectedCard.display_name : 'Selecione um cartao'}
                </h2>
              </div>

              {!selectedCard && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                  Escolha um cartao para ver limite, faturas derivadas e compras mockadas.
                </div>
              )}

              {selectedCard && (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Limite</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">
                        {selectedCard.credit_limit === null ? '--' : currency.format(Number(selectedCard.credit_limit || 0))}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Disponivel projetado</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">
                        {detailLoading || detail?.limits?.available_limit_projected === null || detail?.limits?.available_limit_projected === undefined
                          ? '--'
                          : currency.format(Number(detail.limits.available_limit_projected || 0))}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Fechamento</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">{selectedCard.closing_day || '--'}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm text-slate-500">Vencimento</p>
                      <p className="mt-2 text-xl font-semibold text-slate-900">{selectedCard.due_day || '--'}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                    {(detail?.bills || []).slice(0, 3).map((bill) => (
                      <div key={`${bill.bill_offset}-${bill.closing_date}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                        <p className="text-sm font-semibold text-slate-900">{bill.label}</p>
                        <p className="mt-2 text-2xl font-semibold text-slate-900">{currency.format(Number(bill.projected_amount || 0))}</p>
                        <div className="mt-3 space-y-1 text-sm text-slate-500">
                          <p>Base: {currency.format(Number(bill.base_amount || 0))}</p>
                          <p>Novos gastos: {currency.format(Number(bill.new_spend_amount || 0))}</p>
                          <p>Fecha em: {formatDate(bill.closing_date)}</p>
                          <p>Vence em: {formatDate(bill.due_date)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_1fr]">
            <div className="card">
              <div className="card-body space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mock spend</p>
                  <h2 className="text-xl font-semibold text-slate-900">Registrar compra</h2>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Descricao</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      value={transactionForm.description}
                      onChange={(event) => setTransactionForm((current) => ({ ...current, description: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Merchant</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      value={transactionForm.merchant_name}
                      onChange={(event) => setTransactionForm((current) => ({ ...current, merchant_name: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Valor total</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      type="number"
                      min="0"
                      step="0.01"
                      value={transactionForm.amount_total}
                      onChange={(event) => setTransactionForm((current) => ({ ...current, amount_total: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Parcelas</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      type="number"
                      min="1"
                      max="24"
                      value={transactionForm.installment_count}
                      onChange={(event) => setTransactionForm((current) => ({ ...current, installment_count: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Data da compra</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      type="date"
                      value={transactionForm.purchase_date}
                      onChange={(event) => setTransactionForm((current) => ({ ...current, purchase_date: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Categoria</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      value={transactionForm.category}
                      onChange={(event) => setTransactionForm((current) => ({ ...current, category: event.target.value }))}
                    />
                  </label>
                </div>

                <button className="btn-primary" onClick={handleCreateTransaction} disabled={savingTransaction || !selectedCardId}>
                  {savingTransaction ? 'Salvando...' : 'Adicionar compra mockada'}
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-body space-y-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Simulation</p>
                  <h2 className="text-xl font-semibold text-slate-900">Simular compra</h2>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Descricao</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      value={simulationForm.description}
                      onChange={(event) => setSimulationForm((current) => ({ ...current, description: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Merchant</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      value={simulationForm.merchant_name}
                      onChange={(event) => setSimulationForm((current) => ({ ...current, merchant_name: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Valor total</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      type="number"
                      min="0"
                      step="0.01"
                      value={simulationForm.amount_total}
                      onChange={(event) => setSimulationForm((current) => ({ ...current, amount_total: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Parcelas</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      type="number"
                      min="1"
                      max="24"
                      value={simulationForm.installment_count}
                      onChange={(event) => setSimulationForm((current) => ({ ...current, installment_count: event.target.value }))}
                    />
                  </label>
                  <label className="space-y-2 text-sm md:col-span-2">
                    <span className="font-medium text-slate-700">Data da compra</span>
                    <input
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm"
                      type="date"
                      value={simulationForm.purchase_date}
                      onChange={(event) => setSimulationForm((current) => ({ ...current, purchase_date: event.target.value }))}
                    />
                  </label>
                </div>

                <button className="btn-secondary" onClick={handleSimulatePurchase} disabled={simulating || !selectedCardId}>
                  {simulating ? 'Simulando...' : 'Simular impacto na fatura'}
                </button>

                {simulation && (
                  <div className="space-y-3 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
                    <p className="text-sm font-semibold text-emerald-900">Impacto projetado</p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border border-emerald-200 bg-white p-3">
                        <p className="text-sm text-slate-500">Fatura atual</p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">{currency.format(Number(simulation.current_bill_delta || 0))}</p>
                      </div>
                      <div className="rounded-2xl border border-emerald-200 bg-white p-3">
                        <p className="text-sm text-slate-500">Proxima fatura</p>
                        <p className="mt-1 text-lg font-semibold text-slate-900">{currency.format(Number(simulation.next_bill_delta || 0))}</p>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600">
                      <p>
                        Limite disponivel apos compra:{' '}
                        {simulation.available_limit_after_purchase === null || simulation.available_limit_after_purchase === undefined
                          ? 'Nao informado'
                          : currency.format(Number(simulation.available_limit_after_purchase || 0))}
                      </p>
                      <p>
                        Utilizacao apos compra:{' '}
                        {simulation.utilization_after_purchase === null || simulation.utilization_after_purchase === undefined
                          ? 'Nao informado'
                          : `${Number(simulation.utilization_after_purchase || 0)}%`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Historico</p>
                <h2 className="text-xl font-semibold text-slate-900">Compras mockadas e agenda de faturas</h2>
              </div>

              {!selectedCard && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                  Selecione um cartao para ver compras e agenda de impacto.
                </div>
              )}

              {selectedCard && (
                <div className="space-y-3">
                  {detailLoading && <p className="text-sm text-slate-500">Carregando compras...</p>}
                  {!detailLoading && (!detail?.transactions || detail.transactions.length === 0) && (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Nenhuma compra mockada registrada para este cartao.
                    </div>
                  )}
                  {(detail?.transactions || []).map((transaction) => {
                    const merchantLabel = transaction.normalized_merchant_name || transaction.merchant_name;
                    const categoryLabel = transaction.normalized_category || transaction.category;

                    return (
                      <div key={transaction.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-900">{transaction.description}</p>
                            <p className="mt-1 text-sm text-slate-500">
                              {formatDate(transaction.purchase_date)}
                              {merchantLabel ? ` / ${merchantLabel}` : ''}
                            </p>
                            {transaction.normalized_merchant_name && transaction.merchant_name && transaction.normalized_merchant_name !== transaction.merchant_name && (
                              <p className="mt-1 text-xs text-slate-400">Origem: {transaction.merchant_name}</p>
                            )}
                          </div>
                          <span className="badge-info">{transaction.installment_count}x</span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-600">
                          <span>Valor: {currency.format(Number(transaction.amount_total || 0))}</span>
                          <span>Status: {transaction.status}</span>
                          <span>Categoria: {categoryLabel || 'Nao informada'}</span>
                          {transaction.normalized_category && transaction.category && transaction.normalized_category !== transaction.category && (
                            <span>Categoria original: {transaction.category}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceCardsPage;
